import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../ui/Icon';
import { useInitiateTopUp, useRefreshWallet } from '../../hooks/useWallet';
import { checkTopUpStatusApi } from '../../api/wallet';
import { useToast } from '../../context/ToastContext';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24; // 2 minutes

const TopUpModal = ({ onClose }) => {
  const [amount, setAmount]   = useState('');
  const [phone, setPhone]     = useState('');
  const [amtErr, setAmtErr]   = useState('');
  const [phoneErr, setPhoneErr] = useState('');

  // 'idle' | 'polling' | 'success' | 'failed' | 'timeout'
  const [stage, setStage]     = useState('idle');
  const [pollMsg, setPollMsg] = useState('');

  const pollRef   = useRef(null);
  const pollCount = useRef(0);
  const refIdRef  = useRef(null);

  const topUpMutation = useInitiateTopUp();
  const refreshWallet = useRefreshWallet();
  const { success } = useToast();

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const startPolling = useCallback((referenceId) => {
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current += 1;

      if (pollCount.current > MAX_POLLS) {
        stopPolling();
        setStage('timeout');
        return;
      }

      try {
        const res = await checkTopUpStatusApi(referenceId);
        if (res.status === 'completed') {
          stopPolling();
          setStage('success');
          refreshWallet();
          success('Wallet topped up successfully!');
          setTimeout(onClose, 1500);
        } else if (res.status === 'failed') {
          stopPolling();
          setStage('failed');
          setPollMsg(res.message || 'Payment was declined. Please try again.');
        }
        // 'pending' → keep polling
      } catch {
        // network hiccup — keep polling silently
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, refreshWallet, success, onClose]);

  const validate = () => {
    let ok = true;
    setAmtErr('');
    setPhoneErr('');

    const num = parseFloat(amount);
    if (!amount || isNaN(num) || num < 1) {
      setAmtErr('Minimum top-up is GHS 1');
      ok = false;
    }
    const ghPhone = /^(0\d{9}|233\d{9})$/;
    if (!phone.trim() || !ghPhone.test(phone.trim())) {
      setPhoneErr('Enter a valid Ghana number (e.g. 0244000000)');
      ok = false;
    }
    return ok;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    topUpMutation.mutate(
      { amount: parseFloat(amount), phone: phone.trim() },
      {
        onSuccess: (data) => {
          refIdRef.current = data.referenceId;
          setStage('polling');
          startPolling(data.referenceId);
        },
        onError: (e) => {
          setAmtErr(e?.message || 'Failed to initiate top-up. Please try again.');
        },
      }
    );
  };

  const handleRetry = () => {
    stopPolling();
    setStage('idle');
    setPollMsg('');
    setAmtErr('');
    setPhoneErr('');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 animate-fadeIn"
        style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
        onClick={stage === 'polling' ? undefined : onClose}
      />

      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-surface-800 animate-sheetUp"
        style={{ boxShadow: '0 -8px 40px rgba(0,0,0,0.18)', maxHeight: '92vh', overflowY: 'auto' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1.5 rounded-full bg-surface-500" />
        </div>

        <div className="px-5 pt-3 pb-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #4338ca, #6366f1)' }}
              >
                <Icon name="wallet" size={18} className="text-white" />
              </div>
              <div>
                <h2 className="font-syne font-bold text-slate-800 text-lg leading-tight">Top Up Wallet</h2>
                <p className="font-dm text-surface-300 text-xs">MoMo payment prompt</p>
              </div>
            </div>
            {stage !== 'polling' && (
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center text-surface-200 press-scale"
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>

          {/* ── IDLE / ERROR stage ── */}
          {(stage === 'idle' || stage === 'failed' || stage === 'timeout') && (
            <div className="space-y-4">
              {stage === 'failed' && (
                <div className="rounded-2xl p-4 border border-rose-200 bg-rose-50 flex items-start gap-3">
                  <span className="text-rose-500 mt-0.5">
                    <Icon name="x" size={16} />
                  </span>
                  <p className="font-dm text-sm text-rose-700 leading-snug">{pollMsg}</p>
                </div>
              )}
              {stage === 'timeout' && (
                <div className="rounded-2xl p-4 border border-amber-200 bg-amber-50 flex items-start gap-3">
                  <span className="text-amber-500 mt-0.5">
                    <Icon name="clock" size={16} />
                  </span>
                  <p className="font-dm text-sm text-amber-700 leading-snug">
                    Payment timed out. Please try again.
                  </p>
                </div>
              )}

              {/* Amount */}
              <div>
                <label className="block font-dm font-medium text-slate-700 text-sm mb-1.5">Amount (GHS)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-syne font-bold text-brand-500 text-sm">GHS</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={amount}
                    onChange={(e) => { setAmount(e.target.value); setAmtErr(''); }}
                    placeholder="0.00"
                    className={`w-full pl-14 pr-4 py-3.5 rounded-2xl border font-syne font-bold text-slate-800 text-lg bg-surface-700 outline-none transition-colors
                      ${amtErr ? 'border-rose-400 focus:border-rose-500' : 'border-surface-500 focus:border-brand-400'}`}
                  />
                </div>
                {amtErr && <p className="font-dm text-xs text-rose-500 mt-1.5 ml-1">{amtErr}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block font-dm font-medium text-slate-700 text-sm mb-1.5">MoMo Phone Number</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-300">
                    <Icon name="phone" size={16} />
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneErr(''); }}
                    placeholder="e.g. 0244000000"
                    className={`w-full pl-11 pr-4 py-3.5 rounded-2xl border font-dm text-slate-800 text-sm bg-surface-700 outline-none transition-colors
                      ${phoneErr ? 'border-rose-400 focus:border-rose-500' : 'border-surface-500 focus:border-brand-400'}`}
                  />
                </div>
                {phoneErr && <p className="font-dm text-xs text-rose-500 mt-1.5 ml-1">{phoneErr}</p>}
                <p className="font-dm text-xs text-surface-300 mt-1.5 ml-1">The MoMo number that will be charged</p>
              </div>

              <button
                onClick={handleRetry && stage !== 'idle' ? handleRetry : handleSubmit}
                disabled={topUpMutation.isPending}
                className="w-full py-4 rounded-2xl font-dm font-semibold text-sm text-white press-scale transition-opacity disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 60%, #818cf8 100%)',
                  boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
                }}
              >
                {topUpMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner /> Sending…
                  </span>
                ) : stage !== 'idle' ? 'Try Again' : 'Send Payment Prompt'}
              </button>
            </div>
          )}

          {/* ── POLLING stage ── */}
          {stage === 'polling' && (
            <div className="flex flex-col items-center py-6 gap-5">
              <div className="relative w-20 h-20">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #4338ca, #818cf8)',
                    animation: 'spin 1.2s linear infinite',
                    mask: 'radial-gradient(circle at center, transparent 60%, black 62%)',
                    WebkitMask: 'radial-gradient(circle at center, transparent 60%, black 62%)',
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="phone" size={28} className="text-brand-500" />
                </div>
              </div>
              <div className="text-center">
                <p className="font-syne font-bold text-slate-800 text-base mb-1">Waiting for approval</p>
                <p className="font-dm text-surface-300 text-sm leading-relaxed max-w-[260px] mx-auto">
                  A payment prompt has been sent to <strong className="text-slate-700">{phone}</strong>.
                  Approve it on your phone to credit your wallet.
                </p>
              </div>
              <div className="w-full rounded-2xl p-4 border border-surface-500 bg-surface-700">
                <p className="font-dm text-xs text-surface-300 text-center">
                  Checking every 5 seconds · Times out in 2 minutes
                </p>
              </div>
            </div>
          )}

          {/* ── SUCCESS stage ── */}
          {stage === 'success' && (
            <div className="flex flex-col items-center py-8 gap-4">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(16,185,129,0.12)' }}
              >
                <Icon name="check" size={28} className="text-emerald-500" strokeWidth="2.5" />
              </div>
              <div className="text-center">
                <p className="font-syne font-bold text-slate-800 text-base mb-1">Top-up Successful!</p>
                <p className="font-dm text-surface-300 text-sm">Your wallet has been credited.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

const Spinner = () => (
  <span
    style={{
      display: 'inline-block',
      width: 14,
      height: 14,
      border: '2px solid rgba(255,255,255,0.4)',
      borderTopColor: '#fff',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
    }}
  />
);

export default TopUpModal;
